import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star, CheckCircle, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Star Rating Component
const StarRating = ({ value, onChange, label, required = true }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <Star
              className={`w-8 h-8 ${
                star <= (hover || value)
                  ? "fill-yellow-400 text-yellow-400"
                  : "fill-gray-200 text-gray-200"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default function PublicRatingPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [jobData, setJobData] = useState(null);
  
  // Rating form state
  const [ratings, setRatings] = useState({
    professionalism: 0,
    timeliness: 0,
    work_quality: 0,
    cleanup: 0,
  });
  const [wouldHireAgain, setWouldHireAgain] = useState(null);
  const [reviewText, setReviewText] = useState("");

  useEffect(() => {
    fetchJobData();
  }, [token]);

  const fetchJobData = async () => {
    try {
      const response = await axios.get(`${API}/rate/${token}`);
      setJobData(response.data);
    } catch (err) {
      if (err.response?.status === 400) {
        setError("This rating has already been submitted.");
      } else if (err.response?.status === 404) {
        setError("Rating link not found or expired.");
      } else {
        setError("Unable to load rating page. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all ratings are provided
    if (Object.values(ratings).some(r => r === 0)) {
      toast.error("Please provide a rating for all categories.");
      return;
    }
    
    if (wouldHireAgain === null) {
      toast.error("Please indicate if you would hire again.");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/rate/${token}`, {
        ...ratings,
        would_hire_again: wouldHireAgain,
        review_text: reviewText || null,
      });
      
      setSubmitted(true);
      toast.success("Thank you for your rating!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit rating. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#0FA3B1] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Unable to Load</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Success state - rating submitted
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            Your feedback helps other homeowners make informed decisions and helps contractors improve their service.
          </p>
          <p className="text-sm text-gray-500">
            Powered by <span className="font-semibold text-[#0FA3B1]">Contractor Hub LLC</span>
          </p>
        </div>
      </div>
    );
  }

  // Rating form
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Rate Your Experience
          </h1>
          <p className="text-gray-600">
            How was your experience with <span className="font-semibold text-[#0FA3B1]">{jobData?.contractor_name}</span>?
          </p>
          {jobData?.job_category && (
            <p className="text-sm text-gray-500 mt-1">
              Service: {jobData.job_category}
            </p>
          )}
        </div>

        {/* Rating Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 space-y-6">
          {/* Star Ratings */}
          <StarRating
            label="Professionalism"
            value={ratings.professionalism}
            onChange={(v) => setRatings(prev => ({ ...prev, professionalism: v }))}
          />
          
          <StarRating
            label="Timeliness"
            value={ratings.timeliness}
            onChange={(v) => setRatings(prev => ({ ...prev, timeliness: v }))}
          />
          
          <StarRating
            label="Work Quality"
            value={ratings.work_quality}
            onChange={(v) => setRatings(prev => ({ ...prev, work_quality: v }))}
          />
          
          <StarRating
            label="Cleanup"
            value={ratings.cleanup}
            onChange={(v) => setRatings(prev => ({ ...prev, cleanup: v }))}
          />

          {/* Would Hire Again */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Would you hire again? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={wouldHireAgain === true ? "default" : "outline"}
                onClick={() => setWouldHireAgain(true)}
                className={wouldHireAgain === true ? "bg-green-500 hover:bg-green-600" : ""}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={wouldHireAgain === false ? "default" : "outline"}
                onClick={() => setWouldHireAgain(false)}
                className={wouldHireAgain === false ? "bg-red-500 hover:bg-red-600" : ""}
              >
                No
              </Button>
            </div>
          </div>

          {/* Written Review */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Written Review <span className="text-gray-400">(optional)</span>
            </label>
            <Textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share details about your experience..."
              rows={4}
              maxLength={1000}
              className="resize-none"
            />
            <p className="text-xs text-gray-400 text-right">
              {reviewText.length}/1000 characters
            </p>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-3"
            data-testid="submit-rating-btn"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              "Submit Rating"
            )}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Powered by <span className="font-semibold text-[#0FA3B1]">Contractor Hub LLC</span>
        </p>
      </div>
    </div>
  );
}
